import React, { useState, useCallback } from 'react';
import { PerformanceMonitor, AdaptiveDpr, AdaptiveEvents } from '@react-three/drei';
import { useXR } from '@react-three/xr';

interface VRPerformanceManagerProps {
  children: React.ReactNode;
}

/**
 * VRPerformanceManager — adaptive quality using drei's PerformanceMonitor.
 *
 * AdaptiveDpr is disabled during XR sessions to prevent screen door effect.
 * The XR compositor manages its own resolution — scaling the base renderer
 * resolution down causes visible pixels when stretched to fill the headset.
 */

// Context so child components can read the current quality level
export const QualityContext = React.createContext<number>(1);

const VRPerformanceManager: React.FC<VRPerformanceManagerProps> = ({ children }) => {
  const [quality, setQuality] = useState(1);
  const isPresenting = useXR((s) => !!s.session);

  const handleIncline = useCallback(() => {
    setQuality((q) => Math.min(1, q + 0.1));
  }, []);

  const handleDecline = useCallback(() => {
    setQuality((q) => Math.max(0.3, q - 0.1));
  }, []);

  return (
    <QualityContext.Provider value={quality}>
      <PerformanceMonitor
        onIncline={handleIncline}
        onDecline={handleDecline}
        flipflops={3}
        onFallback={() => setQuality(0.5)}
      />
      {/* AdaptiveDpr disabled in VR — causes screen door effect on Quest */}
      {!isPresenting && <AdaptiveDpr pixelated />}
      <AdaptiveEvents />
      {children}
    </QualityContext.Provider>
  );
};

export default VRPerformanceManager;
